import { normalizeIngredientName } from '../services/groceryListService';
import { resolveIngredient, type IngredientRef } from '../utils/resolveIngredient';
import { INGREDIENT_ENTRIES } from '../utils/ingredientVocabulary';
import { parseIngredientText } from '../utils/ingredientParser';

/**
 * Fixed list of pantry staples assumed to always be on hand (spec: "sel,
 * poivre, huile, épices courantes"). These never appear on the grocery list
 * and count as "have" when crossing a recipe with the pantry.
 *
 * THE staple list — there is no other. The recipe screen, the grocery list and
 * the pantry write path all decide through `stapleKeyForLine` below (#796).
 * The ingredient vocabulary used to carry its own `staple` flags, and the two
 * lists disagreed: the recipe page sent the cook to buy bay leaves, « Je l'ai »
 * then refused to shelve them because the pantry called them a staple, and the
 * toast said « déjà au garde-manger » about a pantry that had none.
 *
 * Deliberately narrower than weekGeneratorService.PANTRY_STAPLES (a scoring
 * heuristic): fresh herbs, garlic, ginger, milk, pasta, rice, etc. are things
 * people actually buy, so they are NOT staples here.
 *
 * Matching is exact on normalizeIngredientName() output — never substring —
 * so "bell pepper" doesn't match the "pepper" staple.
 */

export interface StapleGroup {
  key: string; // Stable slug, language-neutral (e.g., 'olive-oil')
  match: string[]; // Raw forms to match (FR + EN)
  rawPhrase?: boolean; // true for EN pepper-spice forms that need phrase-tier matching
}

// 27 enumerated staple groups covering all 75 + 10 RAW_STAPLES and RAW_PHRASE_STAPLES forms
export const STAPLE_GROUPS: StapleGroup[] = [
  // Salt & pepper
  {
    key: 'salt',
    match: ['salt', 'sel', 'sea salt', 'sel marin', 'fleur de sel', 'gros sel', 'salt and pepper', 'sel et poivre'],
  },
  {
    key: 'pepper',
    match: [
      // FR forms (normalized tier)
      'poivre', 'poivre noir', 'poivre du moulin',
      // EN spice forms (rawPhrase tier — must be exact phrases, never substring)
      'pepper', 'black pepper', 'white pepper',
      'ground pepper', 'ground black pepper', 'ground white pepper',
      'freshly ground pepper', 'freshly ground black pepper',
      'cracked pepper', 'cracked black pepper',
    ],
    rawPhrase: true, // EN forms matched on raw-phrase tier
  },
  // Oils
  {
    key: 'oil',
    match: ['oil', 'huile'],
  },
  {
    key: 'olive-oil',
    match: ['olive oil', "huile d'olive", 'extra virgin olive oil', "huile d'olive extra vierge"],
  },
  {
    key: 'vegetable-oil',
    match: ['vegetable oil', 'huile végétale', 'canola oil', 'huile de canola'],
  },
  // Baking basics
  {
    key: 'flour',
    match: ['flour', 'farine', 'all-purpose flour', 'farine tout usage'],
  },
  {
    key: 'cornstarch',
    match: ['cornstarch', 'fécule', 'fécule de maïs'],
  },
  {
    key: 'sugar',
    match: ['sugar', 'sucre'],
  },
  {
    key: 'brown-sugar',
    match: ['brown sugar', 'cassonade'],
  },
  {
    key: 'baking-soda',
    match: ['baking soda', 'bicarbonate de soude'],
  },
  {
    key: 'vanilla-extract',
    match: ['vanilla extract', 'extrait de vanille'],
  },
  // Dried herbs & spices
  {
    key: 'oregano',
    match: ['oregano', 'origan', 'dried oregano', 'origan séché'],
  },
  {
    key: 'thyme',
    match: ['thyme', 'thym', 'dried thyme', 'thym séché'],
  },
  {
    key: 'basil',
    match: ['dried basil', 'basilic séché'],
  },
  {
    key: 'parsley',
    match: ['dried parsley', 'persil séché'],
  },
  {
    key: 'cumin',
    match: ['cumin', 'ground cumin', 'cumin moulu'],
  },
  {
    key: 'coriander',
    match: ['ground coriander', 'coriandre moulue'],
  },
  {
    key: 'paprika',
    match: ['paprika', 'smoked paprika', 'paprika fumé'],
  },
  {
    key: 'cinnamon',
    match: ['cinnamon', 'cannelle'],
  },
  {
    key: 'nutmeg',
    match: ['nutmeg', 'muscade'],
  },
  {
    key: 'cayenne',
    match: ['cayenne', 'poivre de cayenne'],
  },
  {
    key: 'garam-masala',
    match: ['garam masala'],
  },
  {
    key: 'turmeric',
    match: ['turmeric', 'curcuma', 'ground turmeric', 'curcuma moulu'],
  },
  {
    key: 'bay-leaf',
    match: ['bay leaf', 'feuille de laurier', 'laurier'],
  },
  {
    key: 'red-pepper-flakes',
    match: ['red pepper flakes', 'flocons de piment'],
  },
  {
    key: 'herbes-de-provence',
    match: ['herbes de provence'],
  },
  {
    key: 'italian-seasoning',
    match: ['italian seasoning', 'assaisonnement italien'],
  },
];

/** Lowercase, trim, collapse whitespace, drop parentheticals and trailing punctuation. */
function lightNormalize(name: string): string {
  return name
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[.,;:]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Trailing qualifier phrases recipes append to seasoning/staple lines
// ("black pepper, plus more to taste", "olive oil, more for drizzling").
// Stripped iteratively so chained qualifiers all come off.
const TRAILING_QUALIFIER =
  /\s*,?\s*(?:plus more(?:\s+(?:to taste|for \w+))?|or more(?:\s+to taste)?|more(?:\s+(?:to taste|for \w+))?|to taste|as needed|if needed|for serving|divided|optional)\s*$/;

function stripTrailingQualifiers(name: string): string {
  let prev;
  do {
    prev = name;
    name = name.replace(TRAILING_QUALIFIER, '').replace(/[.,;:]+$/g, '').trim();
  } while (name !== prev && name.length > 0);
  return name;
}

// Measure phrases recipes put in front of a staple (#637). Stripped ONLY to
// produce an extra exact-match candidate — if the stripped form is not a
// staple the result is unchanged, so this can never remove an ingredient from
// the grocery list. The only failure mode is a false staple, which the
// negative cases in measurePrefix.test.ts pin down.
//
// Deliberately EXCLUDES container/count units (gousse, tranche, boîte, botte,
// feuille): those change identity, not amount. "gousse de vanille" is a
// vanilla bean you buy, and stripping it to "vanille" risks colliding with the
// vanilla-extract group. Nothing is lost by leaving them out — "feuille de
// laurier" is already a listed staple form, and garlic / bacon / tomatoes are
// not staples under any spelling.
//
// The bare "soupe|thé|café" alternatives are the orphan residue left by
// extractIngredientNameFromOriginal, which strips "c." and "à" separately and
// leaves the measure noun as the head word. Part 1 of #637 fixes that at
// source; this stays as the net for any other path that still produces it.
// A leading quantity is optional and only ever consumed WITH a measure word
// after it — a bare "2 tomates" must not strip to "tomates". stapleKeyOf is
// called with the parsed name from the grocery path but with rawer text from
// ingredientStatusService and pantryFoodName, so it has to survive both.
const QUANTITY = "(?:[\\d½¼¾⅓⅔⅛⅜⅝⅞][\\d\\s/.,\\-½¼¾⅓⅔⅛⅜⅝⅞]*)?\\s*(?:de\\s+|d'|d’)?";

const MEASURE_PREFIX = new RegExp(
  '^\\s*' + QUANTITY + '\\s*(?:une?\\s+|quelques\\s+|a\\s+|an\\s+)?' +
  '(?:' +
    // FR compound spoons, including the "c.à soupe" no-space form
    'c\\.?\\s*(?:à|a)\\s*(?:s\\.|soupe|th[ée]s?|caf[ée]s?|c\\.)' +
    '|cuill[eè](?:re|rée)s?\\s*(?:à|a)\\s*(?:soupe|caf[ée]|th[ée])' +
    // orphan residue (see note above)
    '|soupes?|th[ée]s?|caf[ée]s?' +
    // FR indefinite measures
    '|pinc[ée]es?|poign[ée]es?|filets?|traits?|soup[çc]ons?|louches?' +
    // volume measures the parser normally eats, kept here so a raw line works
    '|tasses?|cups?' +
    // EN
    '|tablespoons?|teaspoons?|tbsps?|tsps?|pinch(?:es)?|dash(?:es)?' +
    '|handfuls?|splash(?:es)?|drizzles?|knobs?|glugs?' +
  ')' +
  "(?:\\s+(?:de|du|des|d'|d’|of)\\s*|\\s*(?:d'|d’)|\\s+)",
  'i'
);

/**
 * Drop leading measure phrases so "2 c. à soupe de farine" can reach the
 * "farine" staple. Module-local on purpose: widening normalizeIngredientName
 * instead would reach pantry matching, dedup keys and the FR-EN table.
 *
 * Iterates to a fixpoint (a line can carry two layers: "Une pincée de fleur de
 * sel"), capped at 3 passes so a pathological input cannot spin. Never returns
 * empty — a strip that would consume the whole string is discarded.
 */
function stripMeasurePrefix(name: string): string {
  let out = name.trim();
  for (let pass = 0; pass < 3; pass++) {
    const next = out.replace(MEASURE_PREFIX, '').trim();
    if (next === out || !next) return out;
    out = next;
  }
  return out;
}

/**
 * Grade / texture / grind descriptors that `normalizeIngredientName` leaves on
 * the name, so the line never reaches its staple group (#639).
 *
 * Three separate reasons a word lands here, all with the same symptom:
 *   - it is simply absent from the shared descriptor list ("fine", "coarse",
 *     "granulated", "unbleached");
 *   - it is in PRESERVE_DESCRIPTORS on purpose, because elsewhere it picks out
 *     a different food ("ground" keeps ground beef apart from beef) — true for
 *     meat, but for a spice "ground cinnamon" IS cinnamon;
 *   - it arrives late: STEP 6.5 maps the French "moulu" to "ground" AFTER
 *     descriptor stripping has run, so FR lines surface as "cannelle ground".
 *
 * Staple-local on purpose, exactly like `stripMeasurePrefix`. Widening the
 * shared list instead would reach pantry matching, dedup keys and the FR-EN
 * table — and measurably does: adding "fine" there rewrites 14 corpus lines,
 * only 5 of them staples, mangling "cream cheese, cold is fine" along the way.
 *
 * Deliberately NOT here:
 *   - "flaky" — flaky finishing salt is a distinct purchase, not the salt you
 *     already own. Hiding it would break the never-hide-an-ingredient rule.
 *   - "icing" / "confectioners" — powdered sugar is a different product.
 *   - "crushed" — "crushed red pepper" is chilli flakes, not black pepper.
 */
// NOT `\b`. JavaScript's \b is defined on ASCII word characters only, so there
// is no boundary after the "é" in "granulé" and the word never matches, while
// "granulée" does — an accent silently deciding whether a French line resolves.
// Unicode classes instead, with the preceding character captured and put back
// rather than a lookbehind, which is the idiom normalizeIngredientName already
// uses and keeps this off Safari's lookbehind support.
const STAPLE_DESCRIPTOR = new RegExp(
  '(^|[^\\p{L}\\p{N}])(?:' +
    // grain / texture. "fines" is spelled out rather than folded into fines?
    // as a reminder that it is the one form the shared list must never strip:
    // "fines herbes" is a food, not a descriptor (see the test of the same name).
    'fines?|fins?|superfine|caster|coarse|' +
    'granulated|granul[ée]{1,2}s?|' +
    // flour grade ("farine tout usage non blanchie")
    '(?:non\\s+)?blanchi[es]{0,2}|unbleached|bleached|' +
    // grind — see the PRESERVE_DESCRIPTORS / STEP 6.5 notes above
    'ground|moulu[es]{0,2}|' +
    // grade ("sweet paprika" / "paprika doux")
    'sweet|doux|douce' +
  ')(?![\\p{L}\\p{N}])',
  'giu'
);

/**
 * Drop grade/texture/grind words so "fine sea salt" can reach the salt staple.
 * Never returns empty: a strip that would consume the whole name is discarded,
 * so "ground" on its own stays "ground" rather than becoming a wildcard.
 */
function stripStapleDescriptors(name: string): string {
  // '$1 ' puts back the separator the pattern had to consume in place of a
  // lookbehind, so two descriptors in a row ("ground ground cumin") both go.
  const out = name.replace(STAPLE_DESCRIPTOR, '$1 ').replace(/\s+/g, ' ').trim();
  return out || name;
}

/**
 * Candidate names to match: the full (qualifier-stripped) name, then
 * progressively dropping trailing comma segments — recipe grammar puts prep
 * notes after the comma ("black pepper, cracked" / "thyme, stems removed").
 * Each candidate is still matched EXACTLY against the sets, so bell/red
 * pepper forms never sneak in via a prefix.
 *
 * Measure-stripped forms are APPENDED, never substituted: every line that
 * matches today keeps matching by the same path it does today, and the strip
 * only ever adds a fallback.
 */
function matchCandidates(light: string): string[] {
  const stripped = stripTrailingQualifiers(light);
  const candidates = [stripped];
  const segments = stripped.split(',').map(s => s.trim()).filter(Boolean);
  for (let i = segments.length - 1; i >= 1; i--) {
    candidates.push(segments.slice(0, i).join(', '));
  }
  for (const candidate of [...candidates]) {
    const demeasured = stripMeasurePrefix(candidate);
    if (demeasured !== candidate) candidates.push(demeasured);
  }
  // Appended, never substituted — same containment as the measure strip above:
  // every line matching today keeps matching by the path it uses today.
  for (const candidate of [...candidates]) {
    const dedescribed = stripStapleDescriptors(candidate);
    if (dedescribed !== candidate) candidates.push(dedescribed);
  }
  return Array.from(new Set(candidates));
}

// Build normalized staple set lazily, grouping by key for efficient lookup
let stapleKeyMap: Map<string, string> | null = null; // normalized form → key
let rawPhraseMap: Map<string, string> | null = null; // raw phrase candidate → key

// Built lazily so the circular import with groceryListService (which uses
// isStapleNormalized) is resolved by call time, not at module evaluation.
function getStapleMaps(): { normalized: Map<string, string>; rawPhrase: Map<string, string> } {
  if (!stapleKeyMap) {
    stapleKeyMap = new Map();
    rawPhraseMap = new Map();

    for (const group of STAPLE_GROUPS) {
      for (const form of group.match) {
        if (group.rawPhrase) {
          // EN pepper forms → rawPhrase tier (exact phrase matching on light normalization)
          rawPhraseMap.set(lightNormalize(form), group.key);
        } else {
          // Everything else → normalized tier
          const normalized = normalizeIngredientName(form);
          if (normalized) {
            stapleKeyMap.set(normalized, group.key);
          }
        }
      }
    }
  }
  return { normalized: stapleKeyMap, rawPhrase: rawPhraseMap! };
}

/**
 * Returns the staple group key if rawName is a staple, null otherwise.
 * Compound lines ("salt and pepper") return the FIRST part's key.
 * Matching behavior must be byte-identical to the original isStaple / isStapleNormalized.
 */
export function stapleKeyOf(rawName: string): string | null {
  const { normalized, rawPhrase } = getStapleMaps();

  function stapleKeySingle(name: string): string | null {
    for (const candidate of matchCandidates(lightNormalize(name))) {
      if (rawPhrase.has(candidate)) return rawPhrase.get(candidate) || null;
      const normalizedForm = normalizeIngredientName(candidate);
      if (normalizedForm && normalized.has(normalizedForm)) {
        return normalized.get(normalizedForm) || null;
      }
    }
    return null;
  }

  const key = stapleKeySingle(rawName);
  if (key) return key;

  // Check compound lines ("salt and pepper", "sel et poivre")
  const light = stripTrailingQualifiers(lightNormalize(rawName));
  const parts = light.split(/\s+(?:and|et)\s+|\s*&\s*/).map(s => s.trim()).filter(Boolean);
  if (parts.length >= 2 && parts.every(p => stapleKeySingle(p))) {
    // All parts are staples → return the first part's key
    const firstKey = stapleKeySingle(parts[0]);
    return firstKey;
  }

  return null;
}

/**
 * Exact match against an already-normalized ingredient name. NOTE: cannot
 * recognize the EN pepper spice (normalizes to the same form as "poivron") —
 * callers that have the raw name should also check isStaple(rawName).
 */
export function isStapleNormalized(normalizedName: string): boolean {
  const { normalized } = getStapleMaps();
  return normalized.has(normalizedName);
}

/**
 * Key lookup against an already-normalized name — the group-aware counterpart
 * of `isStapleNormalized`.
 *
 * Callers that hold both forms must try this AND `stapleKeyOf(rawName)`. The
 * two tiers catch different things and neither is a superset:
 *
 *   "¼ de tasse de fécule de maïs" → name "tasse de fécule de maïs" → raw MISS,
 *                                    normalized "fécule maï"        → HIT
 *   "black pepper"                 → normalized "pepper" (= poivron) → MISS,
 *                                    raw-phrase tier                → HIT
 *
 * Checking only the raw tier drops measured French lines back onto the grocery
 * list, which is the regression this exists to prevent.
 */
export function stapleKeyOfNormalized(normalizedName: string): string | null {
  const { normalized } = getStapleMaps();
  return normalized.get(normalizedName) ?? null;
}

/**
 * Position of a staple group in declaration order — the canonical sort for
 * anything user-facing.
 *
 * Shared so the Already-Have rows and the banner's example list cannot drift
 * apart: the banner quotes the first three staples, and if the two sorted
 * differently it would name staples that are not the first three on screen.
 * Unknown keys sort last rather than throwing.
 */
export function stapleRank(key: string | undefined | null): number {
  if (!key) return Number.MAX_SAFE_INTEGER;
  const index = STAPLE_GROUPS.findIndex(group => group.key === key);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function isStapleSingle(rawName: string): boolean {
  return stapleKeyOf(rawName) !== null;
}

/**
 * Checks the raw-phrase tier, then the normalized set, on the name and its
 * comma-head candidates. A compound seasoning line ("kosher salt and black
 * pepper") is a staple only when EVERY "and"-part is itself a staple.
 */
export function isStaple(rawName: string): boolean {
  return stapleKeyOf(rawName) !== null;
}

/**
 * Herbs sold fresh are measured in sprigs and bunches; the staples list means
 * the jar. "4 sprigs thyme" and « 4 brins de thym » have to be bought, while a
 * bare "thyme" is assumed on the spice shelf. The French line only reached the
 * list by accident before #772, when its key was « brins thym ».
 */
const FRESH_MEASURE = /^(?:sprigs?|bunch(?:es)?|handfuls?|brins?|branches?|bottes?|bouquets?|poign[ée]es?)$/i;

export function boughtFresh(unit?: string): boolean {
  return !!unit && FRESH_MEASURE.test(unit.trim());
}

/**
 * Words that make the line a different purchase from the jar on the shelf,
 * checked on the whole line before the resolved-id tier may call it a staple.
 * The vocabulary resolves all of these to the staple's id — "30 ml thym frais"
 * is `thyme`, "1 cinnamon stick" is `cinnamon`, « 1 gousse de vanille » is
 * `vanilla-extract` — and every one of them is bought (see MEASURE_PREFIX and
 * the header: fresh herbs are not staples). The text tiers never matched them,
 * and the id tier must not start to.
 */
const OTHER_PURCHASE = new RegExp(
  '(^|[^a-z])(?:' +
    'fresh|frais|fraiches?|whole|entiers?|entieres?|' +
    'sticks?|batons?|gousses?|pods?|beans?|' +
    'sprigs?|brins?|branches?|tiges?|bunch(?:es)?|bottes?|bouquets?' +
  ')(?![a-z])'
);

/**
 * Lines the text tiers leave off on purpose and the vocabulary resolves anyway.
 * "crushed red pepper" is `red-pepper-flakes` to the vocabulary, but it can be a
 * crushed fresh pepper, so it stays on the grocery list (see the staple
 * descriptor note above and groceryListService.test.ts).
 */
const AMBIGUOUS_FOR_ID_TIER = /(^|[^a-z])crushed red peppers?(?![a-z])/;

function foldAccents(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Below this the resolver is guessing at the head of a longer name — « eau de
 * rose », « sucre glace » and « graines de cumin » all resolve at 0.65 to
 * water, sugar and cumin. A staple decision is never made on a guess.
 */
const CONFIDENT = 0.9;

let idKeyMap: Map<string, string> | null = null;

/**
 * Vocabulary id -> staple group, for the ids whose plain name IS a staple in
 * both languages. Derived, not hand-kept, so the vocabulary cannot drift from
 * STAPLE_GROUPS again. `basil` does not map: only DRIED basil is a staple, and
 * the id means the fresh herb.
 */
function getIdKeyMap(): Map<string, string> {
  if (!idKeyMap) {
    idKeyMap = new Map();
    for (const entry of Object.values(INGREDIENT_ENTRIES)) {
      const en = entry.en[0] ? stapleKeyOf(entry.en[0]) : null;
      const fr = entry.fr[0] ? stapleKeyOf(entry.fr[0]) : null;
      if (en && fr && en !== fr) continue;
      const key = en ?? fr;
      if (key) idKeyMap.set(entry.id, key);
    }
  }
  return idKeyMap;
}

/** Staple group of a vocabulary id, for one part of a compound line. */
export function stapleKeyOfId(id: string | null | undefined): string | null {
  return id ? getIdKeyMap().get(id) ?? null : null;
}

/** A single, confident resolution — the only kind the id tier trusts. */
function confidentRef(ref: IngredientRef, text: string): boolean {
  return (
    ref.confidence >= CONFIDENT &&
    ref.components.length === 0 &&
    !OTHER_PURCHASE.test(foldAccents(text)) &&
    !AMBIGUOUS_FOR_ID_TIER.test(foldAccents(text))
  );
}

function stapleKeyOfText(text: string): string | null {
  return stapleKeyOf(text) ?? stapleKeyOfNormalized(normalizeIngredientName(text));
}

/**
 * Is this recipe line assumed on hand, and as which staple group?
 *
 * The one decision — the recipe screen's `staple` state, the grocery list's
 * "already have" and the pantry's refusal to shelve a staple all call this with
 * the same line, so « à acheter » on one screen can never be « basique » on
 * another (#796).
 *
 * The text tiers come first and are exactly `stapleKeyOf`. The resolved-id tier
 * is what catches the forms recipes actually print and the list does not spell
 * out: « 3 feuilles de laurier », "2 bay leaves", "dried oregano leaves".
 */
export function stapleKeyForLine(line: string, unit?: string): string | null {
  const text = (line ?? '').trim();
  if (!text) return null;

  // Memoised: the planner's rankings ask coverage about every line of the
  // catalogue, and every line the pantry does not cover lands here.
  const cacheKey = `${text}\u0000${unit ?? ''}`;
  const cached = LINE_CACHE.get(cacheKey);
  if (cached !== undefined) return cached;
  const key = decideStapleKey(text, unit);
  if (LINE_CACHE.size >= LINE_CACHE_MAX) LINE_CACHE.clear();
  LINE_CACHE.set(cacheKey, key);
  return key;
}

const LINE_CACHE = new Map<string, string | null>();
/**
 * Big enough for a whole library: the catalogue alone has ~11.6k distinct
 * lines and the founder household's index ~14k. At 5000 the cache was cleared
 * several times inside one ranking pass, so every line was decided again on
 * every « Remplacer » (#865). `stapleCacheCapacity` is what the test reads.
 */
const LINE_CACHE_MAX = 30_000;
export const stapleCacheCapacity = (): number => LINE_CACHE_MAX;

function decideStapleKey(text: string, unit?: string): string | null {
  const ref = resolveIngredient(text);
  if (boughtFresh(unit ?? ref.unit)) return null;

  // The line, then the food it names. A bare count in front of a compound
  // ("2 Poivre et sel") is not a measure word, so only the parsed remainder
  // reaches the compound rule — the grocery list always tested that remainder.
  const remainder = parseIngredientText(text).remainder.trim();
  const byText =
    stapleKeyOfText(text) ?? (remainder && remainder !== text ? stapleKeyOfText(remainder) : null);
  if (byText) return byText;

  // A line naming two foods (« Sel et poivre au goût ») is a staple when every
  // food it names is — read through the resolver's split, the one the recipe
  // screen reads it through.
  const parts = partsOf(ref);
  if (parts.length > 1) {
    const keys = parts.map(part => stapleKeyForLine(part));
    return keys.every(Boolean) ? keys[0] : null;
  }

  return confidentRef(ref, text) ? stapleKeyOfId(ref.canonicalId) : null;
}

/** The text of each food a compound line names, or [] when it names one. */
function partsOf(ref: IngredientRef): string[] {
  if (ref.components.length === 0) return [];
  return [ref.raw, ...ref.components.map(part => part.raw)].map(part => part.trim()).filter(Boolean);
}

/**
 * Tap water. Not a staple — nothing to toggle in Basiques — and not a grocery:
 * a line asking for it is always covered and never listed. Same confidence
 * rule as above, so sparkling, coconut and rose water are still things you buy.
 */
export function isTapWater(line: string): boolean {
  const text = (line ?? '').trim();
  if (!text) return false;
  const cached = WATER_CACHE.get(text);
  if (cached !== undefined) return cached;
  const ref = resolveIngredient(text);
  const water = ref.canonicalId === 'water' && ref.confidence >= CONFIDENT && ref.components.length === 0;
  if (WATER_CACHE.size >= LINE_CACHE_MAX) WATER_CACHE.clear();
  WATER_CACHE.set(text, water);
  return water;
}

const WATER_CACHE = new Map<string, boolean>();

/**
 * Every staple group a line needs. `stapleKeyForLine` names a compound line
 * ("Poivre et sel") by its FIRST part, which is enough to call it a staple but
 * not to honour Basiques: with salt turned off, that line has to be bought.
 * Empty when the line is not a staple at all.
 */
export function stapleKeysForLine(line: string, unit?: string): string[] {
  const key = stapleKeyForLine(line, unit);
  if (!key) return [];
  const text = (line ?? '').trim();
  const food = parseIngredientText(text).remainder.trim() || text;
  const resolved = partsOf(resolveIngredient(text));
  const parts = resolved.length > 1
    ? resolved
    : stripTrailingQualifiers(lightNormalize(food))
        .split(/\s+(?:and|et)\s+|\s*&\s*/)
        .map(part => part.trim())
        .filter(Boolean);
  if (parts.length < 2) return [key];
  const keys = parts.map(part => stapleKeyForLine(part, unit));
  return keys.every(Boolean) ? Array.from(new Set(keys as string[])) : [key];
}

/**
 * A staple line counts as on hand unless the cook turned off, in Basiques, any
 * group it needs. An excluded staple is an ordinary ingredient: bought,
 * shelved, matched.
 */
export function assumedOnHand(keys: readonly string[], excludedStaples: readonly string[] = []): boolean {
  return keys.length > 0 && keys.every(key => !excludedStaples.includes(key));
}
